/**
 * Markierung einer angemerkten Passage im gerenderten HTML.
 *
 * Herausgezogen aus PostEditor.renderMarkdown (2026-10-02), damit der
 * Lesemodus normaler Beiträge und der Collab-Lesemodus (Absatz-weise
 * gerendert) dieselbe Logik nutzen.
 *
 * Vorgehen: Markdown-Zeichen aus der Passage entfernen (** __ * _ ` ~ und
 * Überschrift-Präfix), dann zuerst die ganze Passage im HTML suchen. Liegt
 * sie ohne Tags dazwischen vor, wird sie vollständig markiert. Sonst der
 * 30-Zeichen-Präfix: kurz genug, um nach dem Rendering in einem Textknoten
 * zu liegen, lang genug, um eindeutig zu sein. Reine Textsuche, kein Regex
 * auf der Nadel.
 */

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** Bereinigte Suchform einer Markdown-Passage (oder null, wenn zu kurz). */
export function cleanSnippet(raw: string): string | null {
  const cleaned = raw
    .replace(/^#{1,6}\s+/, '')
    .replace(/[*_`~]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return cleaned.length < 3 ? null : cleaned
}

/**
 * Vergleichsform für den Text vor einer Fundstelle: Kleinschreibung,
 * Markdown-Zeichen und Überschrift-Präfixe entfernt, Leerraum gebündelt.
 * Damit lassen sich Markdown-Quelltext und gerenderter Text vergleichen.
 */
export function normalizeContext(text: string): string {
  return text
    .replace(/<[^>]*>/g, ' ')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'")
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*(?:\d{1,3}[.)]|[-*+])\s+/gm, '')
    .replace(/^\s*(?:\{\{TOC\}\}|\+\+\+)\s*$/gm, '')
    .replace(/[*_`~>]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

function commonSuffixLength(a: string, b: string, cap = 60): number {
  let n = 0
  while (n < cap && n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n]) n++
  return n
}

/** Liegt `idx` innerhalb eines HTML-Tags? */
function insideTag(html: string, idx: number): boolean {
  const open = html.lastIndexOf('<', idx)
  if (open < 0) return false
  return html.lastIndexOf('>', idx) < open
}

/**
 * Alle Fundstellen der Nadel (ohne Groß-/Kleinschreibung), in HTML nur
 * außerhalb von Tags.
 */
function occurrences(haystack: string, needle: string, html: boolean): number[] {
  const out: number[] = []
  if (!needle) return out
  const hay = haystack.toLowerCase()
  const nd = needle.toLowerCase()
  let idx = hay.indexOf(nd)
  while (idx >= 0) {
    if (!html || !insideTag(haystack, idx)) out.push(idx)
    idx = hay.indexOf(nd, idx + 1)
  }
  return out
}

/**
 * Wählt unter mehreren Fundstellen diejenige, deren Text davor am besten
 * zu `contextBefore` passt. Anlass (Issue #2338, 08.10.2026): Eine
 * Anmerkung zu „Kurs:" in einer Liste erschien beim ersten „Kurs" im
 * ersten Absatz, weil immer das erste Vorkommen genommen wurde. Ohne
 * Kontext bleibt es beim ersten Vorkommen.
 */
function commonPrefixLength(a: string, b: string, cap = 60): number {
  let n = 0
  while (n < cap && n < a.length && n < b.length && a[n] === b[n]) n++
  return n
}

/** Unter dieser Übereinstimmung zählt ein Kontext als Zufall (ein, zwei Buchstaben). */
const MIN_CONTEXT_SCORE = 4

export function findOccurrence(
  haystack: string,
  needle: string,
  contextBefore: string | undefined,
  html = false,
  contextAfter?: string,
): number {
  const hits = occurrences(haystack, needle, html)
  if (hits.length === 0) return -1
  if (hits.length === 1 || (!contextBefore && !contextAfter)) return hits[0]
  const wantBefore = contextBefore ? normalizeContext(contextBefore).slice(-120) : ''
  const wantAfter = contextAfter ? normalizeContext(contextAfter).slice(0, 120) : ''
  let best = hits[0]
  let bestScore = MIN_CONTEXT_SCORE - 1
  for (const idx of hits) {
    let score = 0
    if (wantBefore) {
      const have = normalizeContext(haystack.slice(Math.max(0, idx - 400), idx))
      score += commonSuffixLength(have, wantBefore)
    }
    if (wantAfter) {
      const end = idx + needle.length
      const have = normalizeContext(haystack.slice(end, end + 400))
      score += commonPrefixLength(have, wantAfter)
    }
    if (score > bestScore) {
      bestScore = score
      best = idx
    }
  }
  return best
}

/**
 * Legt `<mark class="annotation-mark" data-annotation-id=…>` um die
 * Fundstelle der Passage. `contextBefore` ist der Text vor der Passage im
 * Quelltext; mit ihm wird bei mehreren Fundstellen die richtige gewählt.
 * Gibt das HTML unverändert zurück, wenn die Passage nicht gefunden wird.
 */
export function injectAnnotationMark(
  html: string,
  rawSnippet: string,
  annotationId: number | string,
  /** Farbe des Autors; die Markierung trägt sie als `--annot-color`. */
  color?: string | null,
  /** `highlight` = Markierung ohne Notiz: gepunktete Linie, keine Karte. */
  variant: 'note' | 'highlight' = 'note',
  contextBefore?: string,
  contextAfter?: string,
): string {
  const cleaned = cleanSnippet(rawSnippet)
  if (!cleaned) return html
  let needle = escapeHtml(cleaned)
  let idx = findOccurrence(html, needle, contextBefore, true, contextAfter)
  if (idx < 0) {
    needle = cleaned.slice(0, 30)
    idx = findOccurrence(html, needle, contextBefore, true, contextAfter)
  }
  if (idx < 0) return html
  const id = String(annotationId).replace(/"/g, '&quot;')
  const safeColor = color && /^[#a-z0-9(),.% ]+$/i.test(color) ? color : null
  const style = safeColor ? ` style="--annot-color:${safeColor}"` : ''
  return (
    html.slice(0, idx) +
    `<mark class="annotation-mark${variant === 'highlight' ? ' annotation-mark--highlight' : ''}" data-annotation-id="${id}"${style}>` +
    html.slice(idx, idx + needle.length) +
    `</mark>` +
    html.slice(idx + needle.length)
  )
}
